import { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { PanelTopOpen, PanelTopClose, ArrowUpIcon, ArrowDownIcon } from 'lucide-react'
import { gql } from 'graphql-request'

import { useQuery } from '@hooks/graphql'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table'

import Loader from '@components/Loader'
import NoResults from './NoResults'
import InviteEducatorsModal from './InviteEducatorsModal'
import EducatorListActions from './EducatorListActions'
import ExtendTrialsModal from './ExtendTrialsModal'

const journeyStepsKeys = [
  'signedUp',
  'seriesCreated',
  'createdAndAssignedSeries',
  'enrolledStudents',
  'studentsSubmitted',
  'insightsGenerated'
];
const totalJourneySteps = journeyStepsKeys.length;

const journeyStepLabels = {
  signedUp: 'Signed Up',
  seriesCreated: 'Pathway Created',
  createdAndAssignedSeries: 'Pathway Assigned',
  enrolledStudents: 'Enrolled Students',
  studentsSubmitted: 'Students Submitted',
  insightsGenerated: 'Insights Generated'
};

// Helper function to calculate journey progress percentage for an educator's profile.
const getJourneyProgress = (profile) => {
  if (!profile) return 0;
  let completedSteps = 0;
  journeyStepsKeys.forEach(key => {
    if (key === 'signedUp') {
      if (profile.signedUp) completedSteps++;
    } else if (profile.adminActivationMilestones && profile.adminActivationMilestones[key]) {
      completedSteps++;
    }
  });
  return totalJourneySteps > 0 ? (completedSteps / totalJourneySteps) * 100 : 0;
};

const EDUCATORS_LIST_QUERY = gql`
  query organizationEducators($id: ID!) {
    node(id: $id) {
      ... on Organization {
        id
        educators {
          role
          profile {
            id
            fullName
            email
            user {
              id
            }
            adminActivationMilestones {
              seriesCreated
              enrolledStudents
              createdAndAssignedSeries
              studentsSubmitted
              insightsGenerated
            }
          }
        }
        pendingEducatorInvitations {
          email
        }
      }
    }
  }
`;

const EducatorList = ({ organizationId }) => {
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showExtendTrialsModal, setShowExtendTrialsModal] = useState(false)
  const [showFunnel, setShowFunnel] = useState(true)
  const [sortConfig, setSortConfig] = useState({ key: 'journeyProgress', direction: 'descending' });
  const location = useLocation();

  const { isLoading, data: { node: organizationData = {} } = {}, refetch } = useQuery({
    queryKey: ['organizationEducators', organizationId],
    gqlQuery: EDUCATORS_LIST_QUERY,
    variables: { id: organizationId },
    enabled: !!organizationId,
  });

  const educators = organizationData.educators || [];
  const pendingEducatorInvitations = organizationData.pendingEducatorInvitations || [];

  const educatorsWithDetails = useMemo(() => {
    const processedEducators = educators.map(edu => ({
      ...edu,
      profile: {
        ...edu.profile,
        signedUp: true,
      },
      isPendingInvite: false,
      isRealUser: true
    }));

    // Convert pending invites to temporary educators with a unique ID
    const processedPendingInvites = pendingEducatorInvitations.map(invite => ({
      profile: {
        id: `invite-${invite.email}`,
        fullName: "",
        email: invite.email,
        signedUp: false,
        adminActivationMilestones: journeyStepsKeys.reduce((acc, key) => {
          if (key !== 'signedUp') acc[key] = false;
          return acc;
        }, {}),
        user: {}
      },
      role: 'INVITED',
      isPendingInvite: true,
      isRealUser: false
    }));

    return [...processedEducators, ...processedPendingInvites];
  }, [educators, pendingEducatorInvitations]);


  const sortedEducators = useMemo(() => {
    return [...educatorsWithDetails].sort((a, b) => {
      if (sortConfig.key === 'journeyProgress') {
        const progressA = getJourneyProgress(a.profile);
        const progressB = getJourneyProgress(b.profile);
        if (progressA < progressB) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (progressA > progressB) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      }
      // Default sort by name if journeyProgress is equal or not the sort key
      const nameA = a.profile.fullName || '';
      const nameB = b.profile.fullName || '';
      return nameA.localeCompare(nameB) * (sortConfig.direction === 'ascending' ? 1 : -1);
    });
  }, [educatorsWithDetails, sortConfig]);

  const requestSort = (key) => {
    let newDirection;
    if (sortConfig.key === key) {
      newDirection = sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
    } else {
      newDirection = 'ascending';
    }
    setSortConfig({ key, direction: newDirection });
  };

  const totalEducatorsForFunnel = educatorsWithDetails.length;

  const MAX_FUNNEL_VISUAL_HEIGHT_CLASS = 'h-44'
  const SEGMENT_WIDTH_CLASS = 'w-32'
  const pastelColorSequence = ['bg-orange-200', 'bg-red-200', 'bg-pink-300', 'bg-fuchsia-300', 'bg-purple-300', 'bg-violet-300', 'bg-indigo-300', 'bg-blue-300'];

  const funnelStagesData = useMemo(() => {
    return journeyStepsKeys.map(stepKey => ({
      label: journeyStepLabels[stepKey] || stepKey,
      count: educatorsWithDetails.filter(edu => {
        if (stepKey === 'signedUp') return edu.profile.signedUp;
        return edu.profile.adminActivationMilestones && edu.profile.adminActivationMilestones[stepKey];
      }).length,
      key: stepKey
    }));
  }, [educatorsWithDetails, journeyStepsKeys, journeyStepLabels]);

  const funnelDisplayData = useMemo(() => [
    { label: 'Total In System', count: totalEducatorsForFunnel, key: 'totalInSystem' },
    ...funnelStagesData
  ].map((stage, index) => ({
    ...stage,
    color: pastelColorSequence[index % pastelColorSequence.length]
  })), [totalEducatorsForFunnel, funnelStagesData, pastelColorSequence]);

  const funnelSegments = useMemo(() => funnelDisplayData.map((stage, i) => {
    const hNormLeft = totalEducatorsForFunnel > 0 ? (stage.count / totalEducatorsForFunnel) : 0;
    let hNormRight;
    if (i < funnelDisplayData.length - 1) {
      hNormRight = totalEducatorsForFunnel > 0 ? (funnelDisplayData[i + 1].count / totalEducatorsForFunnel) : 0;
    } else {
      hNormRight = hNormLeft * 0.7;
    }
    const topYLeftPercent = ((1 - hNormLeft) / 2) * 100;
    const bottomYLeftPercent = ((1 + hNormLeft) / 2) * 100;
    const topYRightPercent = ((1 - hNormRight) / 2) * 100;
    const bottomYRightPercent = ((1 + hNormRight) / 2) * 100;
    const clipPath = `polygon(${0}% ${topYLeftPercent.toFixed(2)}%, ${100}% ${topYRightPercent.toFixed(2)}%, ${100}% ${bottomYRightPercent.toFixed(2)}%, ${0}% ${bottomYLeftPercent.toFixed(2)}%)`;
    const displayPercentage = (hNormLeft * 100).toFixed(1);
    return { ...stage, clipPath, displayPercentage };
  }), [funnelDisplayData, totalEducatorsForFunnel]);


  if (isLoading) return <div className='flex justify-center items-center h-full'>
    <Loader />
  </div>

  return (
    <div className='my-3'>
      <If condition={!isLoading && showFunnel}>
        <If condition={totalEducatorsForFunnel > 0}>
          <div className="mb-8 p-4 border rounded-lg bg-background dark:border-gray-700 overflow-x-auto">
            <h3 className="text-lg font-semibold mb-6 text-gray-700 dark:text-gray-200 text-center">Pathways Funnel</h3>
            <div className={`flex justify-center items-end py-2 min-w-max`}>
              {funnelSegments.map((segment) => (
                <div key={segment.key} className={`flex flex-col items-center ${SEGMENT_WIDTH_CLASS} mb-4`}>
                  <div
                    className={`relative ${MAX_FUNNEL_VISUAL_HEIGHT_CLASS} w-full ${segment.color}`}
                    style={{ clipPath: segment.clipPath }}
                    title={`${segment.label}: ${segment.count} (${segment.displayPercentage}%)`}
                  >
                  </div>
                  <div className="mt-2 text-center text-xs leading-tight">
                    <p className="font-semibold text-gray-700 dark:text-gray-200">{segment.label}</p>
                    <p className="text-gray-600 dark:text-gray-300">{segment.count} ({segment.displayPercentage}%)</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </If>
      </If>

      <div className='flex flex-col w-full pb-10'>
        <div className='flex justify-between items-center gap-3 mb-3 mr-5 md:mr-0'>
          <Button variant='outline' onClick={() => setShowFunnel(!showFunnel)}>
            {showFunnel ? <PanelTopClose className='h-4 w-4 mr-2' /> : <PanelTopOpen className='h-4 w-4 mr-2' />}
            {showFunnel ? 'Hide Funnel' : 'Show Funnel'}
          </Button>
          <div className="flex gap-3">
            <Button variant='outline' onClick={() => setShowExtendTrialsModal(true)}>Extend trials</Button>
            <Button onClick={() => setShowInviteModal(true)}>Invite educators</Button>
          </div>
        </div>

        <Choose>
          <When condition={educatorsWithDetails.length === 0}>
            <NoResults />
          </When>
          <Otherwise>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px] px-2"></TableHead> {/* Column for Actions icon */}
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead
                      className="min-w-[200px] cursor-pointer"
                      onClick={() => requestSort('journeyProgress')}
                    >
                      <div className="flex items-center">
                        Journey to First Impact
                        {sortConfig.key === 'journeyProgress' && (
                          sortConfig.direction === 'ascending' ? <ArrowUpIcon className="h-4 w-4 ml-1" /> : <ArrowDownIcon className="h-4 w-4 ml-1" />
                        )}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <For each='educator' of={sortedEducators}>
                    <TableRow key={educator.profile.id}>
                      <TableCell className="px-2">
                        {/* Visually disable and prevent interaction with actions for pending invites */}
                        <div className={educator.isPendingInvite ? 'opacity-50 pointer-events-none' : ''}>
                          <EducatorListActions
                            id={educator.profile.id}
                            userId={educator.profile.user?.id}
                            organizationId={organizationId}
                            email={educator.profile.email}
                            fullName={educator.profile.fullName}
                            role={educator.role}
                            refetch={refetch}
                            disabled={educator.isPendingInvite}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <If condition={educator.isRealUser}>
                          <Link className='block' to={`${educator.profile.id}`} state={{ previousListParams: location.search, educatorProfileData: educator.profile }}>
                            {educator.profile.fullName}
                          </Link>
                        </If>
                      </TableCell>
                      <TableCell>
                        <If condition={educator.isRealUser}>
                          <Link className='block' to={`${educator.profile.id}`} state={{ previousListParams: location.search, educatorProfileData: educator.profile }}>
                            {educator.profile.email}
                          </Link>
                        </If>
                        <If condition={!educator.isRealUser}>
                          {educator.profile.email}
                        </If>
                      </TableCell>
                      <TableCell>
                        {educator.role.toLowerCase()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const progressPercentage = getJourneyProgress(educator.profile);
                          // Calculate hue for progress bar color, green (120) for 100%, red (0) for 0%
                          const hue = (progressPercentage / 100) * 120;
                          const barColor = `hsl(${hue.toFixed(0)}, 70%, 50%)`;
                          return (
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mr-2">
                                <div
                                  className="h-2.5 rounded-full"
                                  style={{ width: `${progressPercentage.toFixed(0)}%`, backgroundColor: barColor }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{`${progressPercentage.toFixed(0)}%`}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  </For>
                </TableBody>
              </Table>
            </div>
          </Otherwise>
        </Choose>
      </div>
      <InviteEducatorsModal organizationId={organizationId} isOpen={showInviteModal} closeModal={() => setShowInviteModal(false)} />
      <ExtendTrialsModal organizationId={organizationId} isOpen={showExtendTrialsModal} closeModal={() => setShowExtendTrialsModal(false)} />
    </div>
  )
}

export default EducatorList
